<template>
    <div>
        <section class="problems border-0 observer-fade">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        Project Management Made Simple
                    </h2>
                    <p class="section-subtitle">
                        Traditional project management tools focus on task completion but miss the business side of project delivery that freelancers need.
                    </p>
                </div>
                
                <div class="problems-grid">
                    <div>
                        <p class="text-gray-600 text-center text--lg mb--10 text--semi--bold">
                            Traditional Apps
                        </p>
                        <div class="problem-card steps grid gap-3">
                            <div v-for="item in trad" :key="item.title" class="white-card shadow-md">
                                <div class="icon reminders money">
                                    {{ item.icon }}
                                </div>
                                <div class="flex-1 text-wrap">
                                    <p class="text-gray-800 mb--0 text--sm">
                                        {{ item.title }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="problem-text">
                        vs
                    </div>

                    <div>
                        <p class="text-gray-600 text-center text--lg mb--10 text--semi--bold">
                            Ziner
                        </p>
                        <div class="problem-card steps gap-3 grid">
                            <div v-for="item in ziner" :key="item.title" class="white-card shadow-md">
                                <div class="icon reminders money">
                                    {{ item.icon }}
                                </div><div class="flex-1 text-wrap">
                                    <p class="text-gray-800 mb--0 text--sm">
                                        {{ item.title }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="problems border-0 observer-fade">
            <div class="container">
                <div class="demo-container two-col floating">
                    <div class="demo-container one-col bg-none floating p-0">
                        <div class="demo-header">
                            <div class="traffic-light red" />
                            <div class="traffic-light yellow" />
                            <div class="traffic-light green" />
                            <div class="demo-title">
                                Project Alpha - Sprint 3
                            </div>
                        </div>
                        
                        <div class="tasks-demo">
                            <div class="demo-task-item completed fade-in">
                                <div class="p-3">
                                    <div class="demo-time-display">
                                        <div class="demo-time-counter pulse stat-item">
                                            02:34:15
                                        </div>

                                        <div class="stats-grid">
                                            <div class="stat-item p-3">
                                                <div class="stat-number">
                                                    42h
                                                </div>
                                                <div class="stat-label">
                                                    This Week
                                                </div>
                                            </div>

                                            <div class="stat-item p-3">
                                                <div class="stat-number">
                                                    $3000
                                                </div>
                                                <div class="stat-label">
                                                    Estimated revenue
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="demo-tags">
                                        <div v-for="item in ['Website Redesign', 'Client: TechCorp']" :key="item" class="demo-tag flex align-items-center justify-content-between">
                                            <span class="mr--5">{{ item }}</span>
                                            <IconSvg name="close" /> 
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                
                    <div class="demo-content right-line">
                        <h2>Track to measure project performance</h2> 
                        <p>
                            <strong>Stay productive with time tracking</strong>. Use tracking logs to calculate billing for projects or simply get estimates of your project completion rates.
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <section id="demo" class="demo observer-fade">
            <div class="container">
                <div class="demo-container split two-col bg-none">
                    <div class="demo-visual left">
                        <img src="@/assets/img/task_actions.svg" class="m-w-100 h--auto" alt="screenshot of task management">
                    </div>

                    <div class="demo-content line">
                        <h2>Integrated task management</h2> 
                        <p>
                            <strong>Assign, prioritize and manage tasks per project.</strong>. Manage depencies by linking tasks to related projects, making information retrieval easier.
                        </p>
                        <div class="features-grid mt--15 gap-3">
                            <div v-for="item in automateItems" :key="item.icon" class="feature-item flex align-items-center">
                                <div class="">
                                    {{ item.icon }}
                                </div>
                                <span class="ml--10">{{ item.title }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section id="demo" class="demo observer-fade">
            <div class="container">
                <div class="demo-container two-col split bg-none">
                    <div class="demo-visual filters bg-transparent gap-3 grid p-4">
                        <div v-for="item in filterItems" :key="item.title" class="demo-visual-filter gap-2">
                            <div class="flex">
                                <span class="mr--10 text--sm text-gray-600">{{ item.title }}:</span>
                                <span class="status__map text--xs tag tag--blue bold badge rounded-pill">
                                    <span class="tag--blue__text" />
                                    {{ item.filter }}
                                </span>
                            </div>
                            <span class="status__map text--xs tag tag--grey bold badge rounded-pill">
                                <span class="tag--grey__text" />
                                {{ item.query }}
                            </span>
                            <div>
                                <IconSvg name="x" fill="#6b7280" /> 
                            </div>
                        </div>
                    </div>

                    <div class="demo-content right-line">
                        <h2>Advanced task filters</h2> 
                        <p>
                            <strong>Make use of our advanced filters to organize your tasks</strong>. Group tasks by priority, deadline, labels or date. Identify bottlenecks with a filtered view and close gaps easily.
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <section id="demo" class="demo observer-fade">
            <div class="container">
                <div class="demo-container two-col">
                    <div class="demo-content">
                        <h2>More benefits...</h2>
                        <p>
                            Deliver great projects and build a sustainable business with our easy to use project management feature. Built in time-tracker, linked tasks, and many more.  
                        </p>
                        <ul class="demo-features">
                            <li v-for="item in tools" :key="item">
                                <span class="check-icon">✓</span>{{ item }}
                            </li>
                        </ul>

                        <BaseButton 
                            label="Try for free →" 
                            size="md"
                            shadow
                            @submit="handleGetStarted"
                        />
                    </div>

                    <div class="demo-visual">
                        <div class="invoice-preview">
                            <img src="@/assets/img/project_details.png" alt="project details image">
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import { BaseButton } from "@/components/ui/index";
import IconSvg from "../ui/icons/Icon-Svg.vue";

export default {
    name: "ProductProjectManagement",

    components: {
        BaseButton, 
        IconSvg,
    },
    
    data() {
        return {
            features: [
                { icon: "🔄", title: "Project tracking", desc: "Track project progress, meet deadlines and client deliverables." },
                { icon: "🤖", title: "Automated Reminders", desc: "Get project deadlines reminders to ensure you stay on track with your deliverables." },
                { icon: "📱", title: "Project templates", desc: "Get access to reusable templates for recurring client projects." },
            ],
            tools: [
                "Share completed projects publicly",
                "Schedule projects with start dates",
                "Convert rate cards directly to invoices",
                "Linked notes for easy information retrieval",
            ],
            trad: [
                { icon: "➕ ", title: "Create project" },
                { icon: "✅", title: "Manage related clients separately" },
                { icon: "📅", title: "Create tasks and set deadlines" },
                { icon: "🔄", title: "Use a different app for invoicing" },
            ],
            ziner: [
                { icon: "📐", title: "Scope projects with on-demand rate cards, billing estimates,...etc" },
                { icon: "👤", title: "Link and manage related clients in one place" },
                { icon: "⚡", title: "Link tasks. Set deadline reminders. Monitor project hours. Analyze your performance" },
                { icon: "💻", title: "Send invoices directly from completed projects" },
            ],
            observer: null,
            automateItems: [
                { icon: "👥", title: "Automate recurring tasks" },
                { icon: "💬", title: "Daily custom reminders" },
                { icon: "📋", title: "Set task deadlines" },
            ],
            filterItems: [
                { title: "Filter", filter: "projects", query: "Project Alpha" },
                { title: "Group", filter: "priority", query: "highest" },
            ],
        };
    },

    computed: {
        currency() {
            return this.$store.getters["settings/general"]?.currency;
        },
    },
     
    async mounted() {
        this.initPageIntersectionObserver();
    },
    
    beforeDestroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
    },
    
    methods: {
        handleGetStarted() {            
            this.$store.dispatch("app/setSelectedPlan", "free");
            this.$router.push("/auth/signup");
        },
        initPageIntersectionObserver() {
            this.observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("in-view");
                    }
                });
            }, {
                threshold: 0.1,
                rootMargin: "0px 0px -50px 0px",
            });

            // Observe all elements with observer-fade class
            document.querySelectorAll(".observer-fade").forEach(el => {                
                this.observer.observe(el);
            });
        },
    },
};
</script>

<style lang="scss" scoped>
    .stats {
        background: white;
        padding: 4rem 0;
        border-top: 1px solid #e5e7eb;
    }

    .problems-grid {
        grid-template-columns: 2fr 1fr 2fr;
        max-width: 88%;
        margin: auto;
    }

    .problem-text {
        color: var(--grey-800);
        font-weight: 700;
        font-size: 3rem;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .stat {
        padding: 1rem;
    }

    .white-card .icon {
        font-size: 16px;
    }

    @media (max-width: 768px) {
        .demo-container {
            grid-template-columns: 1fr;
            gap: 3rem;
        }

        .features-grid {
            grid-template-columns: 1fr;
        }

        .problems-grid {
            grid-template-columns: 1fr;
        }
    }

    .fade-in {
        opacity: 0;
        transform: translateY(30px);
        animation: fadeInUp 0.8s ease forwards;
    }
</style>